<template>
  <ComboboxRoot
    :model-value="selectedKeys"
    v-model:search-term="inputValue"
    :multiple="true"
    :filter-function="passthrough"
    class="relative"
    @update:model-value="onKeysUpdate"
    @update:open="onOpenChange"
  >
    <ComboboxAnchor
      class="relative flex flex-wrap items-center gap-1 w-full rounded-md border border-input bg-background pl-2 pr-7 py-1.5 text-sm ring-offset-background focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 min-h-[38px] cursor-text"
      @click="focusInput"
    >
      <!-- Selected pill tags -->
      <span
        v-for="item in modelValue"
        :key="item[trackBy]"
        class="inline-flex items-center gap-1 rounded-full bg-blue-50 border border-blue-200 text-blue-700 px-2 py-0.5 text-xs font-medium"
      >
        {{ item[labelField] }}
        <button
          type="button"
          class="text-blue-500 hover:text-blue-700 focus:outline-none leading-none"
          @click.stop="removeTag(item)"
          tabindex="-1"
          aria-label="Remove"
        >
          ×
        </button>
      </span>

      <ComboboxInput
        ref="inputRef"
        class="flex-1 min-w-[80px] bg-transparent outline-none placeholder:text-muted-foreground text-sm"
        :placeholder="modelValue.length === 0 ? placeholder : ''"
      />

      <!-- Spinner/chevron: absolutely positioned so it stays vertically centred
           regardless of how many pill rows the anchor wraps to -->
      <div class="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center w-4 h-4">
        <svg
          v-if="loading"
          class="animate-spin h-4 w-4 text-blue-400"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
        <ComboboxTrigger v-else class="text-gray-400 hover:text-gray-600 focus:outline-none">
          <ChevronDown class="h-4 w-4" />
        </ComboboxTrigger>
      </div>
    </ComboboxAnchor>

    <ComboboxPortal>
      <ComboboxContent
        class="z-50 w-[var(--radix-combobox-trigger-width)] mt-1 bg-white border border-gray-200 rounded-md shadow-md"
        :side-offset="4"
        position="popper"
      >
        <ComboboxViewport class="max-h-60 overflow-y-auto p-1">
          <ComboboxEmpty class="py-2 px-3 text-sm text-gray-500 text-center">
            {{ loading ? 'Loading...' : (inputValue ? 'No results found.' : 'No options available.') }}
          </ComboboxEmpty>
          <ComboboxItem
            v-for="option in displayOptions"
            :key="option[trackBy]"
            :value="option[trackBy]"
            class="flex items-center gap-2 px-2 py-1.5 text-sm rounded-sm cursor-pointer outline-none data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground"
          >
            <ComboboxItemIndicator class="flex items-center justify-center w-4 h-4 shrink-0">
              <Check class="h-3.5 w-3.5 text-blue-600" />
            </ComboboxItemIndicator>
            <span class="flex-1">{{ option[labelField] }}</span>
          </ComboboxItem>
        </ComboboxViewport>

        <!-- Loading progress bar — shown at bottom of dropdown while fetching -->
        <div v-if="loading" class="px-3 py-1.5 border-t border-gray-100">
          <div class="h-0.5 w-full bg-gray-100 rounded-full overflow-hidden">
            <div class="h-full bg-blue-400 rounded-full origin-left animate-loading-bar" />
          </div>
        </div>

        <!-- Truncation notice -->
        <div
          v-else-if="options.length > maxVisible"
          class="px-3 py-1.5 text-xs text-gray-400 border-t border-gray-100 text-center"
        >
          Showing {{ maxVisible }} of {{ options.length }} — type to narrow results
        </div>
      </ComboboxContent>
    </ComboboxPortal>
  </ComboboxRoot>
</template>

<script>
import { computed, ref, watch } from 'vue';
import {
  ComboboxRoot,
  ComboboxAnchor,
  ComboboxInput,
  ComboboxTrigger,
  ComboboxPortal,
  ComboboxContent,
  ComboboxViewport,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxItemIndicator,
} from 'radix-vue';
import { ChevronDown, Check } from 'lucide-vue-next';

const MAX_VISIBLE = 200;
const SEARCH_DEBOUNCE_MS = 300;

const debounce = (fn, delay) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), delay);
  };
};

export default {
  name: 'MultiSelectCombobox',
  components: {
    ComboboxRoot,
    ComboboxAnchor,
    ComboboxInput,
    ComboboxTrigger,
    ComboboxPortal,
    ComboboxContent,
    ComboboxViewport,
    ComboboxEmpty,
    ComboboxItem,
    ComboboxItemIndicator,
    ChevronDown,
    Check,
  },
  props: {
    modelValue: {
      type: Array,
      default: () => [],
    },
    options: {
      type: Array,
      default: () => [],
    },
    placeholder: {
      type: String,
      default: 'Select...',
    },
    labelField: {
      type: String,
      default: 'value',
    },
    trackBy: {
      type: String,
      default: 'value',
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'search'],
  setup(props, { emit }) {
    const inputRef = ref(null);
    const inputValue = ref('');

    // Selected items are always shown at top; fill remaining slots with non-selected options
    const displayOptions = computed(() => {
      const selectedSet = new Set(props.modelValue.map((s) => s[props.trackBy]));
      const nonSelected = props.options
        .filter((o) => !selectedSet.has(o[props.trackBy]))
        .slice(0, MAX_VISIBLE);
      return [...props.modelValue, ...nonSelected];
    });
    const maxVisible = MAX_VISIBLE;

    // Pass primitive keys to ComboboxRoot so it can compare with ===
    const selectedKeys = computed(() =>
      props.modelValue.map((s) => s[props.trackBy])
    );

    // Disable Radix Vue's internal text filtering — parent handles it via @search
    const passthrough = (options) => options;

    // When Radix Vue emits a new set of keys, map them back to full objects
    const onKeysUpdate = (newKeys) => {
      const updated = newKeys.map((key) => {
        return (
          props.options.find((o) => o[props.trackBy] === key) ||
          props.modelValue.find((s) => s[props.trackBy] === key)
        );
      }).filter(Boolean);
      emit('update:modelValue', updated);
    };

    const removeTag = (item) => {
      const updated = props.modelValue.filter(
        (s) => s[props.trackBy] !== item[props.trackBy]
      );
      emit('update:modelValue', updated);
    };

    const onOpenChange = (open) => {
      if (open) {
        // Emit the initial load directly — do NOT touch inputValue here.
        // onOpenChange fires asynchronously; clearing inputValue at this point
        // would wipe the first character the user has already typed.
        emit('search', '');
      } else {
        // Clear on close so the next open starts with a clean input.
        inputValue.value = '';
      }
    };

    // Watch inputValue (driven by v-model:search-term on ComboboxRoot) and debounce API calls
    const debouncedEmitSearch = debounce((term) => {
      emit('search', term);
    }, SEARCH_DEBOUNCE_MS);

    watch(inputValue, (val) => {
      debouncedEmitSearch(val);
    });

    const focusInput = () => {
      const el = inputRef.value;
      if (el) {
        const input = el.$el ?? el;
        if (typeof input.focus === 'function') input.focus();
      }
    };

    return {
      inputRef,
      inputValue,
      displayOptions,
      maxVisible,
      selectedKeys,
      passthrough,
      onKeysUpdate,
      removeTag,
      onOpenChange,
      focusInput,
    };
  },
};
</script>

<style>
@keyframes loading-bar {
  0%   { transform: scaleX(0.1); opacity: 1; }
  50%  { transform: scaleX(0.7); opacity: 1; }
  100% { transform: scaleX(1);   opacity: 0; }
}

.animate-loading-bar {
  animation: loading-bar 1s ease-in-out infinite;
}
</style>
